import { useState, useEffect } from "react";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

import MDBox from "../../../components/MDBox";
import MDModal from "../../../components/MDModal";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";

import { postDevice, putDevice } from "../../../api/devices";

import { getOrder } from "../../../api/orders";

import PropTypes from "prop-types";

import { useDispatch, useSelector } from "react-redux";

import DeviceGeneral from "./DeviceGeneral";
import DeviceTechnical from "./DeviceTechnical";
import DevicePreparation from "./DevicePreparation";

import { setCurrentOrder } from "../../../redux/reducers/ordersReducer";

import { v4 as uuidv4 } from "uuid";

import { useAuth } from "context/AuthContext";
import useAuthCheck from "config/useAuthCheck";

// Fonction utilitaire pour convertir la date ISO vers le format MySQL
function formatDateForMySQL(isoDate) {
  if (!isoDate) return null;

  // Convertir la date ISO en format MySQL (YYYY-MM-DD HH:MM:SS)
  const date = new Date(isoDate);
  return date.toISOString().slice(0, 19).replace("T", " ");
}

export default function CreateEditDevice({ device = null, edit = false, renderStep = null }) {
  useAuthCheck();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  const [deviceId, setDeviceId] = useState("");
  const [deviceNumber, setDeviceNumber] = useState("");
  const [year, setYear] = useState(parseInt(new Date().getFullYear()));
  const [numberOT, setNumberOT] = useState("");
  const [deviceType, setDeviceType] = useState(null);
  const [payload, setPayload] = useState("");
  const [levelNumber, setLevelNumber] = useState("");
  const [facesNumber, setFacesNumber] = useState("");
  const [pylon, setPylon] = useState(null);
  const [wait, setWait] = useState(null);
  const [period, setPeriod] = useState(null);
  const [location, setLocation] = useState(null);
  const [configuration, setConfiguration] = useState(null);
  const [adress, setAdress] = useState("");
  const [ghettoLastStep, setGhettoLastStep] = useState(false);
  const [assemblyStartContractualDate, setAssemblyStartContractualDate] = useState(null);
  const [isLastStepFormValid, setIsLastStepFormValid] = useState(false);

  const currentOrder = useSelector((state) => state.orders.currentOrder);

  useEffect(() => {
    if (edit && device) {
      setDeviceId(device.device_id);
      setDeviceNumber(device.device_number);
      if (device.ot_number) {
        setYear(2000 + parseInt(device.ot_number.match(/OT(\d+)/)[1]));
        if (device.ot_number.match(/_(\d+)/) !== null) {
          setNumberOT(device.ot_number.match(/_(\d+)/)[1]);
        }
      }
      setDeviceType(device.type);
      setPayload(device.payload);
      setLevelNumber(device.level_number);
      setFacesNumber(device.faces_number);
      setPylon(device.pylon);
      setWait(device.wait);
      setPeriod(device.period);
      // Format the date to ensure it's in YYYY-MM-DD format (remove time if present)
      if (device.assembly_start_contractual_date) {
        const dateStr = device.assembly_start_contractual_date.split(' ')[0]; // Remove time part if exists
        setAssemblyStartContractualDate(dateStr);
      } else {
        setAssemblyStartContractualDate(null);
      }
      setLocation(device.location);
      setConfiguration(device.configuration);
      setAdress(device.adress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit, device]);

  async function createDeviceAPI() {
    const device = {
      device_id: uuidv4(),
      device_number: deviceNumber ? deviceNumber : null,
      ot_number:
        year && numberOT
          ? `OT${year - 2000}_${String(numberOT).padStart(8, "0")}`
          : !year && numberOT
          ? `${String(numberOT).padStart(8, "0")}`
          : year && !numberOT
          ? `OT${year - 2000}`
          : null,
      order_id: currentOrder.order_id,
      type: deviceType,
      payload: payload ? payload : null,
      level_number: levelNumber ? levelNumber : null,
      faces_number: facesNumber ? facesNumber : null,
      pylon: pylon ? pylon : null,
      wait: wait ? wait : 10,
      period: period ? period : null,
      assembly_start_contractual_date: formatDateForMySQL(assemblyStartContractualDate),
      location: location ? location : null,
      configuration: configuration ? configuration : null,
      adress: adress ? adress : null,
    };
    await postDevice(device);
  }

  async function updateDeviceAPI() {
    const device = {
      device_number: deviceNumber ? deviceNumber : null,
      ot_number:
        year && numberOT
          ? `OT${year - 2000}_${String(numberOT).padStart(8, "0")}`
          : !year && numberOT
          ? `${String(numberOT).padStart(8, "0")}`
          : year && !numberOT
          ? `OT${year - 2000}`
          : null,
      type: deviceType,
      payload: payload ? payload : null,
      level_number: levelNumber ? levelNumber : null,
      faces_number: facesNumber ? facesNumber : null,
      pylon: pylon ? pylon : null,
      wait: wait ? wait : 10,
      period: period ? period : null,
      assembly_start_contractual_date: formatDateForMySQL(assemblyStartContractualDate),
      location: location ? location : null,
      configuration: configuration ? configuration : null,
      adress: adress ? adress : null,
    };
    await putDevice(device, deviceId);
  }

  function resetData() {
    setDeviceNumber("");
    setYear(parseInt(new Date().getFullYear()));
    setNumberOT("");
    setDeviceType(null);
    setPayload("");
    setLevelNumber("");
    setFacesNumber("");
    setPylon(null);
    setWait(null);
    setPeriod(null);
    setAssemblyStartContractualDate(null);
    setLocation(null);
    setConfiguration(null);
    setAdress("");
  }

  async function fetchOrder() {
    const response = await getOrder(currentOrder.order_id);
    dispatch(setCurrentOrder(response.data));
  }

  async function cancel() {
    if (edit) {
      await fetchOrder();
    } else {
      resetData();
    }
    setStep(0);
    setOpen(false);
  }

  async function submit() {
    if (edit) {
      await updateDeviceAPI();
      await fetchOrder();
      resetData();
    } else {
      await createDeviceAPI();
      await fetchOrder();
      resetData();
    }
    setStep(0);
    setOpen(false);
  }

  const [step, setStep] = useState(0);
  const steps = ["Informations Générales", "Informations Techniques", "Information chantier"];
  let isLastStep = step === steps.length - 1;

  const next = () => setStep(step + 1);
  const previous = () => setStep(step - 1);

  function getStepContent(stepIndex) {
    switch (stepIndex) {
      case 0:
        return (
          <DeviceGeneral
            deviceNumber={deviceNumber}
            setDeviceNumber={setDeviceNumber}
            ghettoLastStep={ghettoLastStep}
            setGhettoLastStep={setGhettoLastStep}
            year={year}
            setYear={setYear}
            numberOT={numberOT}
            setNumberOT={setNumberOT}
            deviceType={deviceType}
            setDeviceType={setDeviceType}
            adress={adress}
            setAdress={setAdress}
            edit={edit}
          />
        );
      case 1:
        return (
          <DeviceTechnical
            deviceType={deviceType}
            payload={payload}
            setPayload={setPayload}
            levelNumber={levelNumber}
            setLevelNumber={setLevelNumber}
            facesNumber={facesNumber}
            setFacesNumber={setFacesNumber}
            pylon={pylon}
            setPylon={setPylon}
            location={location}
            setLocation={setLocation}
            configuration={configuration}
            setConfiguration={setConfiguration}
            area={currentOrder?.area}
            workType={currentOrder?.work_type}
            edit={edit}
          />
        );
      case 2:
        return (
          <DevicePreparation
            user={user}
            wait={wait}
            setWait={setWait}
            period={period}
            setPeriod={setPeriod}
            assemblyStartContractualDate={assemblyStartContractualDate}
            setAssemblyStartContractualDate={setAssemblyStartContractualDate}
            setIsLastStepFormValid={setIsLastStepFormValid}
            edit={edit}
          />
        );
      default:
        return null;
    }
  }

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <>
      {edit ? (
        <IconButton size="medium" disableRipple color="inherit" onClick={() => setOpen(true)}>
          <Icon style={{ color: darkMode ? "#344767" : "#FFF" }}>edit</Icon>
        </IconButton>
      ) : (
        <IconButton size="medium" disableRipple color="inherit" onClick={() => setOpen(true)}>
          <Icon style={{ color: "#FFF" }}>add</Icon>
        </IconButton>
      )}
      <MDModal open={open} onClose={() => setOpen(false)}>
        <MDBox mt={25} mb={9}>
          <Grid container justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox
                  mt={-12}
                  mb={2}
                  mx={2}
                  variant="gradient"
                  sx={{
                    background: darkMode ? "white" : "linear-gradient(195deg, #364c70, #273956)",
                    boxShadow: darkMode ? "0 1px 6px 1px white" : "0 1px 6px 1px #344767",
                  }}
                  borderRadius="lg"
                  textAlign="center"
                >
                  <MDBox mt={2} mb={1}>
                    <MDBox>
                      {edit ? (
                        <MDTypography
                          variant="h3"
                          fontWeight="bold"
                          color={darkMode ? "inherit" : "white"}
                        >
                          Modification de l'appareil
                        </MDTypography>
                      ) : (
                        <MDTypography
                          variant="h3"
                          fontWeight="bold"
                          color={darkMode ? "inherit" : "white"}
                        >
                          Ajouter un nouvel appareil
                        </MDTypography>
                      )}
                    </MDBox>
                    <MDTypography
                      variant="h5"
                      fontWeight="regular"
                      color={darkMode ? "inherit" : "white"}
                    >
                      Veuillez entrer les informations relatives à l'appareil
                    </MDTypography>
                  </MDBox>
                  <Stepper
                    sx={{ boxShadow: "none", backgroundImage: "none" }}
                    activeStep={step}
                    alternativeLabel
                  >
                    {steps.map((label) => (
                      <Step
                        sx={{
                          "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-active":
                            {
                              color: darkMode && !edit && "#344767 !important",
                            },
                          "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-completed":
                            {
                              color: darkMode && !edit && "#344767 !important",
                            },
                        }}
                        key={label}
                      >
                        <StepLabel
                          sx={{
                            "& .MuiStepLabel-label.Mui-active": {
                              color: darkMode && !edit && "#344767 !important",
                            },
                            "& .MuiStepLabel-label.Mui-completed": {
                              color: darkMode && !edit && "#344767 !important",
                            },
                            "& .MuiStepIcon-root.Mui-active": {
                              background: darkMode && !edit && "#344767",
                              fill: darkMode && !edit && "#344767",
                              stroke: darkMode && !edit && "#344767",
                              borderColor: darkMode && !edit && "#344767",
                              boxShadow: darkMode && !edit && "0rem 0rem 0rem 0.125rem #344767",
                            },
                            "& .MuiStepIcon-root.Mui-completed": {
                              background: darkMode && !edit && "#344767",
                              fill: darkMode && !edit && "#344767",
                              stroke: darkMode && !edit && "#344767",
                              borderColor: darkMode && !edit && "#344767",
                              boxShadow: darkMode && !edit && "0rem 0rem 0rem 0.125rem #344767",
                            },
                          }}
                        >
                          {label}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </MDBox>
                <MDBox p={2}>
                  <MDBox>
                    {getStepContent(step)}
                    <MDBox mt={3} width="100%" display="flex" justifyContent="space-between">
                      {step === 0 ? (
                        <MDBox />
                      ) : (
                        <MDButton variant="gradient" color="warning" onClick={previous}>
                          Précédent
                        </MDButton>
                      )}
                      <MDBox textAlign="right">
                        <Grid container spacing={1.5}>
                          <Grid item>
                            <MDButton variant="gradient" color="warning" onClick={cancel}>
                              Annuler
                            </MDButton>
                          </Grid>
                          <Grid item>
                            <MDButton
                              disabled={
                                !deviceType ||
                                !deviceNumber ||
                                !year ||
                                (isLastStep && !isLastStepFormValid)
                              }
                              variant="gradient"
                              color="success"
                              onClick={isLastStep || ghettoLastStep ? submit : next}
                            >
                              {isLastStep || ghettoLastStep ? "Envoyer" : "Suivant"}
                            </MDButton>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDModal>
    </>
  );
}

CreateEditDevice.propTypes = {
  device: PropTypes.object,
  edit: PropTypes.bool.isRequired,
  renderStep: PropTypes.func,
};
